// src/services/dojoPhotoServices.js
import axios from "axios";

const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const apiUrl = `${baseUrl}/api/dojo`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const withCacheBust = (url) => {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${Date.now()}`;
};

/**
 * Upload/Remplace la photo d'un Dojo.
 * - Côté serveur: sharp -> MASTER + WEB (≤1280x800, fit:"inside"), auto-rotate EXIF, format WEBP par défaut.
 * - Retour: { id, dojo_photoURL } où dojo_photoURL est un chemin public (ex: /uploads/photos_dojos/...).
 *
 * @param {number|string} dojoId
 * @param {File|Blob} file
 * @param {Object} [options]
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @param {boolean} [options.cacheBust=true] - ajoute ?v=timestamp pour invalider le cache navigateur
 * @returns {Promise<{id:number, dojo_photoURL:string}>}
 */
export const uploadDojoPhoto = async (dojoId, file, options = {}) => {
  if (!dojoId) throw new Error("dojoId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress, cacheBust = true } = options;

  const form = new FormData();
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(dojoId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // Laisse axios gérer le Content-Type (multipart/form-data + boundary)
        },
        onUploadProgress,
      }
    );

    // data: { id, dojo_photoURL }
    if (cacheBust && data?.dojo_photoURL) {
      data.dojo_photoURL = withCacheBust(data.dojo_photoURL);
    }
    return data;
  } catch (error) {
    console.error("[uploadDojoPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome une photo Dojo existante côté serveur.
 * - Si 'src' est omis, le serveur tente de retrouver le MASTER via dojo_photoURL actuel.
 * - x,y,width,height en pixels relatifs au MASTER.
 * - Retour: { id, dojo_photoURL } (nouvelle version ≤1280x800).
 *
 * @param {number|string} dojoId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src] - chemin public /uploads/... si tu veux recadrer une autre image
 * @param {boolean} [crop.cacheBust=true]
 * @returns {Promise<{id:number, dojo_photoURL:string}>}
 */
export const cropDojoPhoto = async (dojoId, crop = {}) => {
  if (!dojoId) throw new Error("dojoId requis");

  const { x, y, width, height, format, src, cacheBust = true } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
    }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(dojoId)}/photo/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );

    // data: { id, dojo_photoURL }
    if (cacheBust && data?.dojo_photoURL) {
      data.dojo_photoURL = withCacheBust(data.dojo_photoURL);
    }
    return data;
  } catch (error) {
    console.error("[cropDojoPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si l'URL commence par /uploads/ => on préfixe avec baseUrl
 * - Sinon on renvoie tel quel (utile pour anciennes URLs absolues)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
